// 名前とコピーライト（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { NAME_ON, SIZE } from './config.js';
import { txt, txtHtml } from './text.js';
import { $, el } from './util.js';
import { scheduleAutosave } from './storage.js';
import { closeModal, openModalNode } from './dialog.js';
import { touchUI } from './tour.js';
import { setName as setCharaName } from './core/ops.js';

// ---- 名前とコピーライト ----
// 画像の下に名前（押した色）、その下に「(C) CHARAT」（コピーライトは完成の画像だけ。編集中は出さない、ユーザー判断 2026-10-11）。位置・大きさ・書体は本物の CHOCO の完成の画像（1000px）で測った値を
// 500 四方の単位にしたもの: 名前 47.3px・基準線 969、コピーライト 22.7px・基準線 991、どちらも左右のまん中。書体は Makinas5（Makinas-Scrap-5）。
// 本物のコピーライトは「(C)年 CHARAT」だが、モックは年なし（ユーザー判断 2026-10-11）。
// 本物の名前は MENU の「名前入力」で入れ、色は「キャラ」カテゴリでパレットを押すと変わる（分かりにくいので、モックは名前の欄に色も置いた）
export const NAME_FONT = 'Makinas5w'; // ui/style.css の @font-face（woff2 0.7MB。起動では読まず、loadNameFont で）
export const NAME_SIZE = 23.64, NAME_BASE = 484.5, NAME_MAX_W = 480; // 500 四方の単位。はみ出すほど長い名前は横を縮める（本物は fillText の幅の上限）
export const CR_SIZE = 11.33, CR_BASE = 495.5, CR_COLOR = '#333333';
export const COPYRIGHT = '(C) CHARAT';
export const NAME_COLOR = '#333333'; // 名前の色の既定（本物の COLOR_NAME）
export const NAME_MAXLEN = 16;       // 本物の入力欄の maxlength
export function buildNameLayer() {
  const g = el('g', { id: 'nameLayer', 'pointer-events': 'none' });
  S.nameText = el('text', { x: SIZE / 2, y: NAME_BASE, 'text-anchor': 'middle', 'font-family': NAME_FONT, 'font-size': NAME_SIZE });
  g.appendChild(S.nameText);
  S.stageSvg.appendChild(g);
}
export function loadNameFont() {
  // 絵を作るだけのモード（?render=1、「声で動かす」の見えない iframe）は名前を描かないので読まない（0.7MB。2026-10-23）
  if (window.CREATE_RENDER || S.nameFontAsked || !(document.fonts && document.fonts.load)) return;
  S.nameFontAsked = true;
  document.fonts.load(NAME_SIZE + 'px ' + NAME_FONT).then(drawName, () => {});
}
// 名前を SVG に出し、名前の上のボタン（名前が無いときは「＋ 名前」）を合わせる
export function drawName() {
  if (!S.nameText) return;
  // 名前の無いメーカー: 保存データに名前があっても描かない（名前のボタンは page.php が hidden で出す）
  // 絵を作るだけのモードも描かない（「声で動かす」は名前を外して使う。描くと書体を読みにいく）
  const name = NAME_ON && !window.CREATE_RENDER ? C.name[0] || '' : '';
  if (name) loadNameFont();
  S.nameText.textContent = name;
  S.nameText.setAttribute('fill', C.name[1] || NAME_COLOR);
  S.nameText.style.display = name ? '' : 'none';
  S.nameText.removeAttribute('textLength');
  S.nameText.removeAttribute('lengthAdjust');
  let w = name ? S.nameText.getComputedTextLength() : 0;
  if (w > NAME_MAX_W) { S.nameText.setAttribute('textLength', NAME_MAX_W); S.nameText.setAttribute('lengthAdjust', 'spacingAndGlyphs'); w = NAME_MAX_W; }
  const chip = $('nameChip');
  if (!NAME_ON) { chip.hidden = true; return; }
  chip.classList.toggle('has-name', !!name);
  chip.setAttribute('data-name', name); // 全身のメーカーのスマホは、名前のボタンに名前を出す（style.css の .app.tall .name-chip）
  chip.setAttribute('aria-label', name ? txt('name_change', { name: name }) : txt('name_enter'));
  // 名前があるときは、名前の幅に合わせた透明なボタン（キャラの四角に対する割合）
  chip.style.width = name ? 'calc(' + (w / SIZE * 100) + '% + 16px)' : '';
}
export function setName(name: string, color?: string) {
  setCharaName(name, color || NAME_COLOR);
  drawName();
  scheduleAutosave();
}
// 名前の欄（打つとすぐキャラに出る。色もここで）
export function openNameEditor() {
  const root = document.createElement('div');
  root.className = 'name-ed';
  root.innerHTML =
    '<h2>' + txtHtml('name_title') + '</h2>' +
    '<div class="name-field"><label><span class="sr">' + txtHtml('name_title') + '</span><input type="text" maxlength="' + NAME_MAXLEN + '" placeholder="' + txtHtml('name_placeholder') + '" autocomplete="off" enterkeyhint="done"></label>' +
    '<button type="button" class="name-clear" aria-label="' + txtHtml('name_clear') + '" title="' + txtHtml('name_clear') + '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17"/></svg></button></div>' +
    '<p class="note">' + txtHtml('name_caution') + '</p>' +
    '<h3>' + txtHtml('name_color') + '</h3><div class="name-colors"></div>';
  const input = root.querySelector('input')!, colors = root.querySelector<HTMLElement>('.name-colors')!, clear = root.querySelector<HTMLButtonElement>('.name-clear')!;
  input.value = C.name[0] || '';
  input.addEventListener('input', () => { setName(input.value, C.name[1]); clear.hidden = !input.value; });
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); closeModal(); } });
  const drawColors = () => {
    colors.textContent = '';
    const now = (C.name[1] || NAME_COLOR).toLowerCase();
    [NAME_COLOR].concat(C.data.PALLET.map((row) => String(row[0]))).forEach((hex, i) => {
      hex = hex.toLowerCase();
      if (i > 0 && hex === NAME_COLOR) return; // 既定の色はいちばん前だけ
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'sw' + (hex === now ? ' sel' : '');
      sw.style.background = hex;
      sw.title = i === 0 ? txt('color_default') : hex;
      sw.addEventListener('click', () => { setName(C.name[0], hex); drawColors(); });
      colors.appendChild(sw);
    });
  };
  drawColors();
  clear.hidden = !input.value;
  clear.addEventListener('click', () => { input.value = ''; setName('', C.name[1]); clear.hidden = true; input.focus(); });
  openModalNode(root, [], { closeLabel: txt('ok'), light: true });
  // スマホは欄を押してから文字を打つほうが、キーボードで画面がずれにくい
  if (!touchUI) setTimeout(() => { input.focus(); input.select(); }, 50);
}
// 完成の画像に名前とコピーライトを描く（SVG の文字は画像にすると書体が届かないので、本物と同じく canvas に描き直す）
// withName: false ならコピーライトだけ（お披露目に送る画像。finish.ts）
export function drawNameOnCanvas(ctx: CanvasRenderingContext2D, px: number, withName = true) {
  const k = px / SIZE;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  if (withName && NAME_ON && C.name[0]) {
    ctx.font = (NAME_SIZE * k) + 'px ' + NAME_FONT;
    ctx.fillStyle = C.name[1] || NAME_COLOR;
    ctx.fillText(C.name[0], px / 2, NAME_BASE * k, NAME_MAX_W * k);
  }
  ctx.font = (CR_SIZE * k) + 'px ' + NAME_FONT;
  ctx.fillStyle = CR_COLOR;
  ctx.fillText(COPYRIGHT, px / 2, CR_BASE * k);
}

// 名前と (C) CHARAT だけを透明の上に描いた画像（完成画面のアニメ ui/emote.js が、動くキャラの上に重ねる）
export function nameImage(px: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = px;
  drawNameOnCanvas(canvas.getContext('2d')!, px);
  return canvas.toDataURL('image/png');
}
